import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Users, Plus, Trash2, Info } from 'lucide-react';

function D2_ResearchTeam() {
  const { dataE, updateDataRaw, nextStep, previousStep } = useWizard();
  const researchTeam = dataE.researchTeam;

  const updatePI = (field, value) => {
    updateDataRaw({
      'wizardE.researchTeam': {
        ...researchTeam,
        principalInvestigator: {
          ...researchTeam.principalInvestigator,
          [field]: value
        }
      }
    });
  };

  const addSupervisor = () => {
    updateDataRaw({
      'wizardE.researchTeam': {
        ...researchTeam,
        supervisors: [...researchTeam.supervisors, {
          firstName: '',
          lastName: '',
          email: '',
          institution: '',
          role: ''
        }]
      }
    });
  };

  const updateSupervisor = (index, field, value) => {
    const newSupervisors = [...researchTeam.supervisors];
    newSupervisors[index] = { ...newSupervisors[index], [field]: value };
    updateDataRaw({
      'wizardE.researchTeam': { ...researchTeam, supervisors: newSupervisors }
    });
  };

  const removeSupervisor = (index) => {
    const newSupervisors = researchTeam.supervisors.filter((_, i) => i !== index);
    updateDataRaw({
      'wizardE.researchTeam': { ...researchTeam, supervisors: newSupervisors }
    });
  };

  const addCoResearcher = () => {
    updateDataRaw({
      'wizardE.researchTeam': {
        ...researchTeam,
        coResearchers: [...researchTeam.coResearchers, {
          firstName: '',
          lastName: '',
          email: '',
          institution: '',
          role: ''
        }]
      }
    });
  };

  const updateCoResearcher = (index, field, value) => {
    const newCoResearchers = [...researchTeam.coResearchers];
    newCoResearchers[index] = { ...newCoResearchers[index], [field]: value };
    updateDataRaw({
      'wizardE.researchTeam': { ...researchTeam, coResearchers: newCoResearchers }
    });
  };

  const removeCoResearcher = (index) => {
    const newCoResearchers = researchTeam.coResearchers.filter((_, i) => i !== index);
    updateDataRaw({
      'wizardE.researchTeam': { ...researchTeam, coResearchers: newCoResearchers }
    });
  };

  const pi = researchTeam.principalInvestigator;
  const piComplete = pi.firstName && pi.lastName && pi.email && pi.institution;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <Users className="step-icon" size={32} />
        <div>
          <h2>Step 2: Research Team</h2>
          <p className="step-description">
            Provide details about the principal investigator and other team members involved in this research.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Principal Investigator (PI)</h3>
        <p className="section-description">
          The PI is the person with overall responsibility for the conduct of the research.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              value={pi.firstName}
              onChange={(e) => updatePI('firstName', e.target.value)}
              placeholder="Enter first name"
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              value={pi.lastName}
              onChange={(e) => updatePI('lastName', e.target.value)}
              placeholder="Enter last name"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Email Address *</label>
          <input
            type="email"
            value={pi.email}
            onChange={(e) => updatePI('email', e.target.value)}
            placeholder="Enter institutional email"
          />
        </div>

        <div className="form-group">
          <label>Institution / Organization *</label>
          <input
            type="text"
            value={pi.institution}
            onChange={(e) => updatePI('institution', e.target.value)}
            placeholder="e.g., University of Example"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Faculty / School</label>
            <input
              type="text"
              value={pi.faculty}
              onChange={(e) => updatePI('faculty', e.target.value)}
              placeholder="e.g., Faculty of Science"
            />
          </div>
          <div className="form-group">
            <label>Department</label>
            <input
              type="text"
              value={pi.department}
              onChange={(e) => updatePI('department', e.target.value)}
              placeholder="e.g., Department of Psychology"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Role</label>
          <select
            value={pi.role}
            onChange={(e) => updatePI('role', e.target.value)}
          >
            <option value="staff">Staff Member</option>
            <option value="postdoc">Postdoctoral Researcher</option>
            <option value="phd">PhD Student</option>
            <option value="masters">Masters Student</option>
            <option value="undergraduate">Undergraduate Student</option>
            <option value="external">External Researcher</option>
          </select>
        </div>
      </div>

      <div className="form-section">
        <div className="section-header-with-action">
          <div>
            <h3>Supervisors</h3>
            <p className="section-description">
              If you are a student, add your research supervisor(s) here.
            </p>
          </div>
          <button className="btn-add" onClick={addSupervisor}>
            <Plus size={16} /> Add Supervisor
          </button>
        </div>

        {researchTeam.supervisors.length === 0 ? (
          <div className="info-box info">
            <Info size={20} />
            <p>No supervisors added. Click "Add Supervisor" if applicable to your research.</p>
          </div>
        ) : (
          researchTeam.supervisors.map((supervisor, index) => (
            <div key={index} className="team-member-card">
              <div className="card-header">
                <span className="card-title">Supervisor {index + 1}</span>
                <button
                  className="btn-icon-danger"
                  onClick={() => removeSupervisor(index)}
                  title="Remove supervisor"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={supervisor.firstName}
                    onChange={(e) => updateSupervisor(index, 'firstName', e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={supervisor.lastName}
                    onChange={(e) => updateSupervisor(index, 'lastName', e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={supervisor.email}
                    onChange={(e) => updateSupervisor(index, 'email', e.target.value)}
                    placeholder="Email address"
                  />
                </div>
                <div className="form-group">
                  <label>Institution</label>
                  <input
                    type="text"
                    value={supervisor.institution}
                    onChange={(e) => updateSupervisor(index, 'institution', e.target.value)}
                    placeholder="Institution"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="form-section">
        <div className="section-header-with-action">
          <div>
            <h3>Co-Researchers</h3>
            <p className="section-description">
              Add any other researchers who will be involved in data collection or analysis.
            </p>
          </div>
          <button className="btn-add" onClick={addCoResearcher}>
            <Plus size={16} /> Add Co-Researcher
          </button>
        </div>

        {researchTeam.coResearchers.length === 0 ? (
          <div className="info-box info">
            <Info size={20} />
            <p>No co-researchers added. Click "Add Co-Researcher" if applicable.</p>
          </div>
        ) : (
          researchTeam.coResearchers.map((coResearcher, index) => (
            <div key={index} className="team-member-card">
              <div className="card-header">
                <span className="card-title">Co-Researcher {index + 1}</span>
                <button
                  className="btn-icon-danger"
                  onClick={() => removeCoResearcher(index)}
                  title="Remove co-researcher"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={coResearcher.firstName}
                    onChange={(e) => updateCoResearcher(index, 'firstName', e.target.value)}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={coResearcher.lastName}
                    onChange={(e) => updateCoResearcher(index, 'lastName', e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={coResearcher.email}
                    onChange={(e) => updateCoResearcher(index, 'email', e.target.value)}
                    placeholder="Email address"
                  />
                </div>
                <div className="form-group">
                  <label>Role/Contribution</label>
                  <input
                    type="text"
                    value={coResearcher.role}
                    onChange={(e) => updateCoResearcher(index, 'role', e.target.value)}
                    placeholder="e.g., Data collection, Analysis"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={piComplete ? nextStep : null}
        disableNext={!piComplete}
        nextLabel="Continue to Project Synopsis"
      />
    </div>
  );
}

export default D2_ResearchTeam;
